<template>
    <div>
        <card v-if="session">

            <h3 class="mb-0" slot="header">{{ $t('sessions.configuration') }}</h3>

            <!-- Tester 1 is configuring -->
            <div v-show="firstTesterIsConfiguring">

                <validation-observer v-slot="{ invalid, handleSubmit }" ref="sessionValidator" v-if="!form.loading">
                    <form @submit.prevent="handleSubmit(saveConfiguration)" @keydown="form.errors.clear($event.target.name)" class="mb-3">

                        <date-time-input class="mb-3"
                            :label="$t('sessions.slot')"
                            label-classes="form-control-label required"
                            name="slot"
                            rules="required"
                            v-model="form.data.scheduled_at"
                            :error="form.errors.get('scheduled_at')"
                        >
                        </date-time-input>

                        <base-select class="mb-4"
                            :label="$t('common.simulation')"
                            :placeholder="$t('common.simulation')"
                            label-classes="required"
                            v-model="form.data.simulation_id"
                            :options="simulationOptions"
                            @input="form.errors.clear('simulation_id')"
                            :error="form.errors.get('simulation_id')"
                        >
                        </base-select>

                        <base-switch class="mb-3"
                            v-model="resitMode"
                            :label="$t('sessions.resit-mode')"
                        ></base-switch>

                        <base-switch class="mb-3"
                            v-model="form.data.two_testers"
                            :label="$t('sessions.wait-form-second-tester')"
                        ></base-switch>

                        <div class="category form-category mb-4">
                            <small><span class="text-warning">*</span> {{ $t('common.required-fields') }}</small>
                        </div>

                        <div class="text-center">
                            <base-button native-type="submit"
                                type="primary"
                                :disabled="form.errors.any() || invalid"
                                :loading="form.recording"
                                v-if="!form.data.tester1_id || form.data.tester1_id == $auth.user.id"
                            >
                                {{ $t('common.save') }}
                            </base-button>

                            <base-button outline type="primary" @click.prevent="resetSession" class="ml-1"
                                v-if="session.status == 'starting'"
                            >
                                {{ $t('sessions.reset') }}
                            </base-button>

                            <base-button outline type="primary" @click.prevent="startSession" class="ml-1"
                                v-if="firstTesterIsReadyToStart"
                            >
                                {{ $t('sessions.start-online') }}
                            </base-button>

                            <base-button outline type="primary" @click.prevent="startSessionOffline" class="ml-1"
                                v-if="firstTesterIsReadyToStart"
                            >
                                {{ $t('sessions.start-offline') }}
                            </base-button>
                        </div>
                    </form>
                </validation-observer>

                <!-- Waiting for 2nd tester -->
                <div v-if="firstTesterIsWaitingForSecondTester">
                    <base-alert type="warning" class="mb-4">
                        <i class="fas fa-hourglass mr-3"></i> {{ $t('sessions.tester1-waiting-for-second-tester-notice') }}
                    </base-alert>
                </div>

                <!-- 2nd tester ready-->
                <div v-if="firstTesterIsReadyToStart">
                    <base-alert type="success" class="mb-4">
                        <i class="fas fa-thumbs-up mr-3"></i> {{ $t('sessions.tester1-ready-to-start-notice') }}
                    </base-alert>
                </div>
            </div>

            <!-- Tester 1 is completing. -->
            <div v-if="firstTesterIsCompleting">
                <base-alert type="success" class="mb-4">
                    <i class="fas fa-thumbs-up mr-3"></i> {{ $t('sessions.tester1-started-notice') }}
                </base-alert>

                <base-button outline type="primary" @click.prevent="resetSession" class="ml-1">
                    {{ $t('sessions.reset') }}
                </base-button>

                <base-button outline type="primary" @click.prevent="goOffline" class="ml-1" v-if="!session.sync">
                    {{ $t('sessions.go-offline') }}
                </base-button>

                <base-button outline type="primary" @click.prevent="goOnline" class="ml-1" v-else>
                    {{ $t('sessions.go-online') }}
                </base-button>

                <base-button type="primary" @click.prevent="openObservation">
                    {{ $t('sessions.observation') }} <i class="fas fa-chevron-right ml-1"></i>
                </base-button>
            </div>

            <!-- Tester 2 is ready to join -->
            <div v-if="secondTesterCanJoin">
                <p class="mb-4">
                    {{ $t('sessions.tester2-join-notice') }}
                </p>

                <base-button
                    type="primary"
                    :loading="form.recording"
                    @click.prevent="joinSession"
                >
                    {{ $t('sessions.join') }}
                </base-button>
            </div>

            <!-- Tester 2 is waiting for the session to be started -->
            <div v-if="secondTesterIsWaiting">
                <base-alert type="warning" class="mb-4">
                    <i class="fas fa-hourglass mr-3"></i> {{ $t('sessions.tester2-waiting-to-start') }}
                </base-alert>
            </div>

            <!-- Tester 2 is completing. -->
            <div v-if="secondTesterIsCompleting">
                <base-alert type="success" class="mb-4">
                    <i class="fas fa-thumbs-up mr-3"></i> {{ $t('sessions.tester2-started-notice') }}
                </base-alert>

                <base-button outline type="primary" @click.prevent="goOffline" class="ml-1" v-if="!session.sync">
                    {{ $t('sessions.go-offline') }}
                </base-button>

                <base-button outline type="primary" @click.prevent="goOnline" class="ml-1" v-else>
                    {{ $t('sessions.go-online') }}
                </base-button>

                <base-button type="primary" @click.prevent="openObservation">
                    {{ $t('sessions.observation') }} <i class="fas fa-chevron-right ml-1"></i>
                </base-button>
            </div>
        </card>

        <portal to="modals">

            <confirm-modal v-model="modals.confirmReset">
                <p>{{ $t('sessions.reset-confirm') }}</p>
            </confirm-modal>

            <sync-modal v-model="modals.offlineSync" :sync="sync" :autoRemoveAfterSync="true" @done="syncDone">
            </sync-modal>

            <base-modal
                v-model="modals.offlineError"
                size="sm"
                modal-classes="modal-danger"
                modal-content-classes="bg-gradient-danger"
            >
                <div class="py-3 text-center">
                    <i class="tim-icons icon-t-warning icon-3x"></i>
                    <h4 class="heading mt-4">{{ offlineErrorMessage }}</h4>
                </div>
            </base-modal>

        </portal>
    </div>
</template>

<script>
    import CrudClient from '@trax/classes/CrudClient'
    import CrudForm from '@trax/classes/CrudForm'
    import ModalsController from '@trax/classes/ModalsController'
    import SessionFeatures from './SessionFeatures'
    import SyncModal from '../offline/SyncModal'

    export default {

        components: {
            SyncModal
        },

        props: {
            session: {
                type: Object,
                required: true
            },

            simulations: {
                type: Array,
                required: true
            },
        },

        data() {
            let features = new SessionFeatures(this.$router, this.$auth, this.session)
            let client = new CrudClient('/socrates/api/front/dashboard/store/sessions')
            return {
                features,
                form: new CrudForm(client, features.mapData, features.unmapData, {
                    updated: this.updated
                }),
                modals: new ModalsController(['confirmReset', 'offlineSync', 'offlineError']),
                resitMode: false,

                // Offline.
                startOffline: false,
                offlineErrorMessage: null,
                sync: {
                    endpoint: '/socrates/api/front/offline/sessions',
                    params: {
                        session_id: this.session.id,
                        registration_id: this.session.registration_id,
                        simulation_id: this.session.simulation_id,
                        force: false,
                    },
                },
                intervalId: null,
            }
        },

        created() {
            this.modals.init(this, 'modals')
            this.form.init(this, 'form')
            this.form.errors.setValidator('sessionValidator', this)
            this.sessionChanged()
        },

        watch: {
            session() {
                this.sessionChanged()
            }
        },

        computed: {

            simulationOptions() {
                let options = {}
                this.simulations.forEach(item => {
                    options[item.id] = item.name
                })
                return options
            },

            firstTesterIsConfiguring() {
                return this.session.status == 'pending'
                    || (this.session.status == 'starting' && this.$auth.user.id == this.session.tester1_id)
            },

            firstTesterIsWaitingForSecondTester() {
                return this.$auth.user.id == this.session.tester1_id
                    && this.session.status == 'starting'
                    && this.session.two_testers
                    && !this.session.tester2
            },

            firstTesterIsReadyToStart() {
                return this.$auth.user.id == this.session.tester1_id
                    && this.session.status == 'starting'
                    && (!this.session.two_testers || this.session.tester2)    
            },

            firstTesterIsCompleting() {
                return (this.session.status == 'started' || this.session.status == 'completed')
                    && this.$auth.user.id == this.session.tester1_id
            },

            secondTesterCanJoin() {
                return this.session.status == 'starting'
                    && this.$auth.user.id != this.session.tester1_id
                    && this.session.two_testers
                    && !this.session.tester2_id
            },

            secondTesterIsWaiting() {
                return this.session.status == 'starting'
                    && this.$auth.user.id == this.session.tester2_id
            },

            secondTesterIsCompleting() {
                return (this.session.status == 'started' || this.session.status == 'completed')
                    && this.$auth.user.id == this.session.tester2_id
            },
        },

        methods: {
            
            updated(resp) {
                if (resp.data.data.status !== this.session.status && resp.data.data.status == 'started') {
                    if (this.startOffline) {
                        this.downloadSession()
                    } else {
                        // If just started, then go to the observation tab.
                        this.openObservation()
                    }
                } else {
                    this.$emit('reload-session')
                }
            },

            sessionChanged() {
                this.resitMode = this.session.mode == 'resit'
                this.form.update(this.session)
            },

            saveConfiguration() {
                this.form.data.tester1_id = this.$auth.user.id
                this.form.data.status = 'starting'
                this.form.data.mode = this.resitMode ? 'resit' : 'normal'
                this.form.data = { ...this.form.data}
            },

            joinSession() {
                this.form.data.tester2_id = this.$auth.user.id
                this.form.data = { ...this.form.data}
            },

            startSession() {
                this.form.data.status = 'started'
                this.form.data = { ...this.form.data}
            },

            resetSession() {
                this.modals.open('confirmReset').then(resp => {
                    if (resp.confirmed) {
                        if (this.session.sync) {
                            this.removeOfflineSessionAndReset()
                        } else {
                            this.resetSessionNow() 
                        }
                    }
                })
            },

            resetSessionNow() {
                axios.post('/socrates/api/front/dashboard/data/sessions/'+this.session.id+'/reset')
                .then(resp => {
                    this.$emit('reload-session')
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },

            openObservation() {
                this.features.openObservation()
            },


            // ---------- Offline.

            startSessionOffline() {
                this.startOffline = true
                this.startSession()
            },

            goOffline() {
                this.downloadSession(true)
            },

            downloadSession(stayHere = false) {
                this.$loader.show()
                axios.post(this.sync.endpoint, {
                    session_id: this.session.id,
                    registration_id: this.session.registration_id,
                    simulation_id: this.session.simulation_id,
                    force: true
                })
                .then(resp => {
                    this.$loader.hide()
                    if (stayHere) {
                        this.$emit('reload-session')
                    } else {
                        this.openObservation()
                    }
                }).catch(error => {
                    this.$loader.hide()
                    this.handleOfflineError(error)
                })
            },

            goOnline() {
                if (this.session.sync.color == 'danger')  {
                    this.syncSession()
                } else {
                    this.removeOfflineSession()
                }
            },

            syncSession() {
                this.modals.open('offlineSync')
            },

            syncDone() {
                this.removeOfflineSession()
            },

            removeOfflineSession() {
                this.$loader.show()
                axios.delete(this.sync.endpoint, { data: {
                    session_id: this.session.id,
                    registration_id: this.session.registration_id,
                    simulation_id: this.session.simulation_id,
                }})
                .then(resp => {
                    this.$loader.hide()
                    this.$emit('reload-session')
                }).catch(error => {
                    this.$loader.hide()
                    this.handleOfflineError(error)
                })
            },

            removeOfflineSessionAndReset() {
                this.$loader.show()
                axios.delete(this.sync.endpoint, { data: {
                    session_id: this.session.id,
                    registration_id: this.session.registration_id,
                    simulation_id: this.session.simulation_id,
                }})
                .then(resp => {
                    this.$loader.hide()
                    this.resetSessionNow()
                }).catch(error => {
                    this.$loader.hide()
                    this.handleOfflineError(error)
                })
            },

            handleOfflineError(error) {
                if (!error.response) {
                    return
                }
                switch(error.response.status) {
                    case 409:
                        this.modals.open('offlineConflict')
                        break
                    case 403:
                    case 404:
                    case 405:
                    case 444:
                        this.offlineErrorMessage = this.$t('pwa.http-error-' + error.response.status)
                        this.modals.open('offlineError')
                        break
                    default:
                        this.$auth.handleErrorVm(error, this)
                }
            },
        }
    }
</script>
